import { create } from "zustand";
import { persist, createJSONStorage } from "zustand/middleware";
import type { Comment, ID } from "@/shared/types";
import { type Mode, MODES } from "@/shared/mode/types";

type TabHistory = Record<Mode, string[]>;

const defaultTabHistory = (): TabHistory => ({
  instaflix: ['/instaflix'],
  reelsflix: ['/reelsflix'],
  movieflix: ['/movieflix'],
});

type State = {
  likedPostIds: Set<ID>;
  likedReelIds: Set<ID>;
  savedPostIds: Set<ID>;
  myListMovieIds: Set<ID>;
  followedUserIds: Set<ID>;
  comments: Comment[];
  tabHistory: TabHistory;
};

type Actions = {
  togglePostLike: (id: ID) => void;
  toggleReelLike: (id: ID) => void;
  togglePostSave: (id: ID) => void;
  toggleMyList: (id: ID) => void;
  toggleFollow: (id: ID) => void;
  addComment: (input: { target: "post" | "reel"; targetId: ID; text: string }) => void;
  pushTab: (mode: Mode, url: string) => void;
  popTab: (mode: Mode) => string | null;
  clearTab: (mode: Mode) => void;
  lastTabUrl: (mode: Mode) => string;
};

const toggleInSet = (set: Set<ID>, id: ID) => {
  const next = new Set(set);
  if (next.has(id)) next.delete(id);
  else next.add(id);
  return next;
};

const replacer = (_key: string, value: unknown) =>
  value instanceof Set ? { __set: true, values: Array.from(value) } : value;
const reviver = (_key: string, value: unknown) => {
  if (value && typeof value === "object" && (value as { __set?: boolean }).__set) {
    return new Set((value as { values: ID[] }).values);
  }
  return value;
};

export const useStore = create<State & Actions>()(
  persist(
    (set, get) => ({
      likedPostIds: new Set(),
      likedReelIds: new Set(),
      savedPostIds: new Set(),
      myListMovieIds: new Set(),
      followedUserIds: new Set(),
      comments: [],
      tabHistory: defaultTabHistory(),
      togglePostLike: (id) => set((s) => ({ likedPostIds: toggleInSet(s.likedPostIds, id) })),
      toggleReelLike: (id) => set((s) => ({ likedReelIds: toggleInSet(s.likedReelIds, id) })),
      togglePostSave: (id) => set((s) => ({ savedPostIds: toggleInSet(s.savedPostIds, id) })),
      toggleMyList: (id) => set((s) => ({ myListMovieIds: toggleInSet(s.myListMovieIds, id) })),
      toggleFollow: (id) => set((s) => ({ followedUserIds: toggleInSet(s.followedUserIds, id) })),
      addComment: ({ target, targetId, text }) =>
        set((s) => ({
          comments: [
            ...s.comments,
            {
              id: `local-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
              postId: targetId,
              target,
              authorId: "guest",
              text,
              createdAt: new Date().toISOString(),
              likesCount: 0,
            },
          ],
        })),
      pushTab: (mode, url) =>
        set((s) => {
          const stack = s.tabHistory[mode] ?? [];
          // Avoid pushing duplicate of the current top.
          if (stack[stack.length - 1] === url) return {};
          return {
            tabHistory: { ...s.tabHistory, [mode]: [...stack, url] },
          };
        }),
      popTab: (mode) => {
        const stack = get().tabHistory[mode] ?? [];
        if (stack.length <= 1) return null;
        const next = stack.slice(0, -1);
        set((s) => ({ tabHistory: { ...s.tabHistory, [mode]: next } }));
        return next[next.length - 1] ?? null;
      },
      clearTab: (mode) =>
        set((s) => ({
          tabHistory: { ...s.tabHistory, [mode]: [`/${mode}`] },
        })),
      lastTabUrl: (mode) => {
        const stack = get().tabHistory[mode] ?? [];
        return stack[stack.length - 1] ?? `/${mode}`;
      },
    }),
    {
      name: "pikpok-store",
      storage: createJSONStorage(() => localStorage, { replacer, reviver }),
      // Defensive merge: ensure tabHistory has all modes after rehydrate.
      merge: (persisted, current) => {
        const p = (persisted as Partial<State>) ?? {};
        const merged = { ...current, ...p } as State & Actions;
        const th = (p.tabHistory ?? {}) as Partial<TabHistory>;
        const fixed: TabHistory = { ...defaultTabHistory() };
        for (const m of MODES) {
          if (Array.isArray(th[m]) && th[m]!.length > 0) fixed[m] = th[m]!;
        }
        merged.tabHistory = fixed;
        return merged;
      },
    },
  ),
);
